import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { 
  BookOpen, 
  Search, 
  Bookmark, 
  Highlighter, 
  StickyNote, 
  HelpCircle, 
  Menu, 
  X,
  Crown,
  Home,
  Calendar,
  Image as ImageIcon,
  Leaf,
  Map,
  LogIn,
  LogOut,
  Bell,
  BellOff,
  ChevronRight,
  List,
  FileText
} from "lucide-react";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "./ui/sheet";
import { Separator } from "./ui/separator";
import { Switch } from "./ui/switch";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { getBooks } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const Layout = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [books, setBooks] = useState({ ancien_testament: [], nouveau_testament: [] });
  const [selectedBook, setSelectedBook] = useState(null);
  const [openAccordion, setOpenAccordion] = useState(null);
  const [focusMode, setFocusMode] = useState(() => {
    return localStorage.getItem("bible-focus-mode") === "true";
  });
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isPremium, signOut, loading: authLoading } = useAuth();

  const match = location.pathname.match(/\/bible\/([^/]+)(?:\/(\d+))?/);
  const currentBookIdFromUrl = match ? match[1] : null;
  const currentChapterFromUrl = match ? parseInt(match[2], 10) : null;

  useEffect(() => {
    const fetchBooks = async () => {
      try {
        const data = await getBooks();
        setBooks(data);
      } catch (error) {
        console.error("Error fetching books:", error);
      }
    };
    fetchBooks();
  }, []);

  useEffect(() => {
    if (currentBookIdFromUrl && (books.ancien_testament.length > 0 || books.nouveau_testament.length > 0)) {
      setSelectedBook(currentBookIdFromUrl);
      const inAncien = books.ancien_testament?.some(b => b.id === currentBookIdFromUrl);
      const inNouveau = books.nouveau_testament?.some(b => b.id === currentBookIdFromUrl);
      if (inAncien) setOpenAccordion("ancien_testament");
      else if (inNouveau) setOpenAccordion("nouveau_testament");
    }
  }, [currentBookIdFromUrl, books]);

  const toggleFocusMode = (enabled) => {
    setFocusMode(enabled);
    localStorage.setItem("bible-focus-mode", enabled.toString());
    if (enabled) {
      toast.success("Mode concentration activé", { description: "Les notifications sont bloquées" });
    } else {
      toast.info("Mode concentration désactivé");
    }
  };

  const menuItems = [
    { path: "/", label: "Accueil", icon: Home },
    { path: "/recherche", label: "Recherche (mots et thèmes)", icon: Search },
    { path: "/lectures", label: "Lectures du jour", icon: Calendar },
    { path: "/garde-pages", label: "Garde-pages", icon: Bookmark, premium: true },
    { path: "/surlignages", label: "Mes surlignages", icon: Highlighter, premium: true },
    { path: "/notes", label: "Mes notes", icon: StickyNote, premium: true },
    { path: "/notifications", label: "Notifications", icon: Bell, premium: true },
    { path: "/mode-emploi", label: "Mode d'emploi", icon: HelpCircle },
    { path: "/guide-commentaires", label: "Guide des commentaires", icon: BookOpen },
    { path: "/glossaire", label: "Glossaire des abréviations", icon: BookOpen },
    { path: "/premium", label: isPremium ? "Mon compte Premium" : "Passer Premium", icon: Crown, highlight: !isPremium },
  ];

  const documentItems = [
    { path: "/documents/dedicace", label: "Dédicace" },
    { path: "/documents/avant_propos", label: "Avant-propos" },
    { path: "/documents/introduction", label: "Introduction" },
    { path: "/guide-commentaires", label: "Comprendre les commentaires" },
    { path: "/glossaire", label: "Glossaire des abréviations" },
    { path: "/supprimer-compte", label: "Supprimer mes données", danger: true },
  ];

  const isActive = (path) => location.pathname === path;

  const handleBookSelect = (bookId) => {
    setSelectedBook(bookId);
  };

  const handleChapterSelect = (bookId, chapter) => {
    navigate(`/bible/${bookId}/${chapter}`);
    setIsOpen(false);
  };

  const renderBookList = (testament, title) => (
    <AccordionItem value={testament} className="border-none">
      <AccordionTrigger className="px-3 py-2 text-sm font-medium text-ink-primary hover:text-rubric-red hover:no-underline">
        {title}
      </AccordionTrigger>
      <AccordionContent className="pt-0 pb-2">
        <div className="space-y-0.5 pl-3">
          {books[testament]?.map((book) => {
            const isBookSelected = selectedBook === book.id;
            return (
              <div key={book.id} className="relative">
                <div 
                  className={`flex items-center justify-between px-3 py-1.5 text-sm cursor-pointer rounded-sm transition-colors ${
                    isBookSelected ? 'text-rubric-red font-medium bg-paper-dark' : 'text-ink-secondary hover:text-rubric-red'
                  }`}
                  onClick={() => handleBookSelect(isBookSelected ? null : book.id)}
                >
                  <span>{book.name}</span>
                  <ChevronRight 
                    className={`h-4 w-4 transition-transform ${isBookSelected ? 'rotate-90 text-rubric-red' : ''}`} 
                  />
                </div>
                {isBookSelected && (
                  <div className="mt-1 ml-1 mr-1 mb-2 p-2 bg-paper rounded-lg border border-border">
                    <p className="text-[11px] font-medium text-ink-secondary mb-1.5 px-1">
                      Chapitres ({book.chapters}) :
                    </p>
                    <div className="grid grid-cols-5 gap-1.5 max-h-48 overflow-y-auto p-1">
                      {Array.from({ length: book.chapters }, (_, i) => i + 1).map((ch) => {
                        const isCurrentChapter = book.id === currentBookIdFromUrl && ch === currentChapterFromUrl;
                        return (
                          <button
                            key={ch}
                            type="button"
                            onClick={() => handleChapterSelect(book.id, ch)}
                            className={`h-8 rounded text-xs font-medium transition-all flex items-center justify-center touch-manipulation ${
                              isCurrentChapter
                                ? "bg-rubric-red text-white font-bold shadow-sm"
                                : "bg-paper-dark text-ink-primary hover:bg-rubric-red/10 hover:text-rubric-red border border-border/60"
                            }`}
                          >
                            {ch}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </AccordionContent>
    </AccordionItem>
  );

  return (
    <div className="min-h-screen bg-paper flex flex-col">
      {/* Header - Mobile optimized */}
      <header className="sticky top-0 z-40 bg-paper/95 backdrop-blur-sm border-b border-border safe-area-inset-top">
        <div className="container mx-auto px-3 sm:px-4 h-14 sm:h-16 flex items-center justify-between">
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-ink-primary hover:text-rubric-red hover:bg-paper-dark h-10 w-10 touch-manipulation"
                aria-label="Ouvrir le menu"
              >
                <Menu className="h-6 w-6" strokeWidth={1.5} />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[85vw] sm:w-80 p-0 bg-paper border-r border-border">
              <SheetHeader className="p-4 text-left border-b border-border">
                <SheetTitle className="font-heading text-xl text-ink-primary flex items-center justify-between">
                  <span>Bible <span className="text-rubric-red">Fillion</span></span>
                  <span className="text-xs font-decorative text-ink-secondary">1920</span>
                </SheetTitle>
              </SheetHeader>
              
              <ScrollArea className="h-[calc(100vh-5rem)]">
                <div className="p-3">
                  {/* User Profile Section */}
                  <div className="mb-4 p-3 bg-paper-dark rounded-lg border border-border">
                    {user ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gold-leaf/20 text-gold-leaf flex items-center justify-center font-bold">
                            {user.email?.[0].toUpperCase() || "U"}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-ink-primary truncate">{user.email}</p>
                            <span className={`inline-flex items-center text-xs ${isPremium ? "text-gold-leaf" : "text-ink-secondary"}`}>
                              {isPremium && <Crown className="h-3 w-3 mr-1 inline" />}
                              {isPremium ? "Membre Premium" : "Compte Gratuit"}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => { signOut(); setIsOpen(false); }}
                          className="w-full text-left text-xs text-red-600 hover:text-red-700 flex items-center gap-1.5 pt-2 border-t border-border"
                        >
                          <LogOut className="h-4 w-4" />
                          Se déconnecter
                        </button>
                      </div>
                    ) : (
                      <Link
                        to="/connexion"
                        onClick={() => setIsOpen(false)}
                        className="flex items-center gap-3 text-amber-800 hover:text-amber-900"
                      >
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                          <LogIn className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">Se connecter</p>
                          <p className="text-xs text-ink-secondary">Accédez à vos données</p>
                        </div>
                      </Link>
                    )}
                  </div>

                  <Separator className="my-4 bg-border" />

                  {/* Focus Mode Toggle */}
                  <div className="mb-4 p-3 bg-amber-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {focusMode ? (
                          <BellOff className="h-5 w-5 text-amber-600" />
                        ) : (
                          <Bell className="h-5 w-5 text-ink-secondary" />
                        )}
                        <div>
                          <p className="text-sm font-medium text-ink-primary">Mode concentration</p>
                          <p className="text-xs text-ink-secondary">Bloque les notifications</p>
                        </div>
                      </div>
                      <Switch 
                        checked={focusMode} 
                        onCheckedChange={toggleFocusMode}
                      />
                    </div>
                  </div>

                  {/* Main Navigation */}
                  <nav className="space-y-0.5">
                    {menuItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setIsOpen(false)}
                          className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 ${
                            item.highlight 
                              ? "bg-gold-leaf/10 text-gold-leaf hover:bg-gold-leaf/20"
                              : isActive(item.path)
                              ? "bg-paper text-rubric-red"
                              : "text-ink-primary hover:bg-paper hover:text-rubric-red active:scale-[0.98]"
                          }`}
                          data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                        >
                          <Icon className={`h-5 w-5 ${item.highlight ? "text-gold-leaf" : ""}`} strokeWidth={1.5} />
                          <span className="font-body text-sm sm:text-base flex-1">{item.label}</span>
                          {item.premium && !isPremium && (
                            <Crown className="h-4 w-4 text-gold-leaf" />
                          )}
                        </Link>
                      );
                    })}
                  </nav>

                  <Separator className="my-4 bg-border" />

                  {/* Atlas Fillion Section */}
                  <div className="mb-2">
                    <div className="flex items-center gap-2 px-3 py-2">
                      <Map className="h-4 w-4 text-rubric-red" />
                      <h3 className="text-xs uppercase tracking-wider text-rubric-red font-body font-bold">
                        ATLAS FILLION (1920)
                      </h3>
                    </div>
                    <div className="space-y-1 pl-2">
                      <Link
                        to="/gravures"
                        onClick={() => setIsOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                          isActive('/gravures') ? 'bg-red-50 text-rubric-red font-semibold' : 'text-ink-primary hover:bg-paper hover:text-rubric-red'
                        }`}
                      >
                        <ImageIcon className="h-4 w-4 text-rubric-red" />
                        <span className="font-body text-sm font-medium">Atlas archéologique (Planches)</span>
                      </Link>
                      <Link
                        to="/histoire-naturelle"
                        onClick={() => setIsOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                          isActive('/histoire-naturelle') ? 'bg-emerald-50 text-emerald-800 font-semibold' : 'text-ink-primary hover:bg-paper hover:text-emerald-700'
                        }`}
                      >
                        <Leaf className="h-4 w-4 text-emerald-600" />
                        <span className="font-body text-sm font-medium">Atlas d'histoire naturelle</span>
                      </Link>
                      <Link
                        to="/cartes"
                        onClick={() => setIsOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                          isActive('/cartes') ? 'bg-amber-50 text-amber-900 font-semibold' : 'text-ink-primary hover:bg-paper hover:text-amber-800'
                        }`}
                      >
                        <Map className="h-4 w-4 text-emerald-600" />
                        <span className="font-body text-sm font-medium">Atlas géographique (Cartes)</span>
                      </Link>
                    </div>
                  </div>

                  <Separator className="my-4 bg-border" />

                  {/* Table des Matières */}
                  <div className="mb-2">
                    <div className="flex items-center gap-2 px-3 py-2">
                      <List className="h-4 w-4 text-ink-secondary" />
                      <h3 className="text-xs uppercase tracking-wider text-ink-secondary font-body font-medium">
                        Table des Matières
                      </h3>
                    </div>
                  </div>
                  
                  <Accordion type="single" collapsible className="w-full" value={openAccordion} onValueChange={setOpenAccordion}>
                    {renderBookList("ancien_testament", "Ancien Testament")}
                    {renderBookList("nouveau_testament", "Nouveau Testament")}
                  </Accordion>

                  <Separator className="my-4 bg-border" />

                  {/* Documents */}
                  <div className="mb-2">
                    <h3 className="px-3 text-xs uppercase tracking-wider text-ink-secondary font-body">
                      Documents
                    </h3>
                  </div>
                  
                  <nav className="space-y-0.5">
                    {documentItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                          item.danger
                            ? "text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20"
                            : isActive(item.path)
                            ? "bg-paper text-rubric-red"
                            : "text-ink-primary hover:bg-paper hover:text-rubric-red active:scale-[0.98]"
                        }`}
                      >
                        <FileText className="h-4 w-4" strokeWidth={1.5} />
                        <span className="font-body text-sm">{item.label}</span>
                      </Link>
                    ))}
                  </nav>

                  <Separator className="my-4 bg-border" />

                  {/* Quote */}
                  <div className="px-3 py-4 bg-paper rounded-lg">
                    <p className="text-xs text-ink-secondary italic leading-relaxed">
                      « La Bible est le livre du Messie, le livre de Notre-Seigneur Jésus-Christ. »
                    </p>
                    <p className="text-xs text-ink-secondary mt-2"> L.-Cl. Fillion</p>
                  </div>
                </div>
              </ScrollArea>
            </SheetContent>
          </Sheet>

          <Link to="/" className="flex items-center gap-2">
            <h1 className="font-heading text-lg sm:text-xl md:text-2xl text-ink-primary">
              Bible <span className="text-rubric-red">Fillion</span>
            </h1>
            <span className="text-xs text-ink-secondary font-decorative hidden sm:inline">1920</span>
          </Link>

          {/* Desktop Direct Links */}
          <div className="hidden md:flex items-center gap-4 lg:gap-6">
            <Link 
              to="/gravures" 
              className={`text-sm font-medium flex items-center gap-1.5 transition-colors ${
                isActive('/gravures') ? 'text-rubric-red font-semibold' : 'text-ink-primary hover:text-rubric-red'
              }`}
            >
              <ImageIcon className="h-4 w-4 text-rubric-red" />
              <span>Atlas archéologique</span>
            </Link>
            <Link 
              to="/histoire-naturelle" 
              className={`text-sm font-medium flex items-center gap-1.5 transition-colors ${
                isActive('/histoire-naturelle') ? 'text-emerald-800 font-semibold' : 'text-ink-primary hover:text-emerald-700'
              }`}
            >
              <Leaf className="h-4 w-4 text-emerald-600" />
              <span>Atlas d'histoire naturelle</span>
            </Link>
            <Link 
              to="/cartes" 
              className={`text-sm font-medium flex items-center gap-1.5 transition-colors ${
                isActive('/cartes') ? 'text-emerald-700 font-semibold' : 'text-ink-primary hover:text-emerald-700'
              }`}
            >
              <Map className="h-4 w-4 text-emerald-600" />
              <span>Atlas géographique</span>
            </Link>
            <Link 
              to="/guide-commentaires" 
              className={`text-sm font-medium flex items-center gap-1.5 transition-colors ${
                isActive('/guide-commentaires') ? 'text-rubric-red font-semibold' : 'text-ink-primary hover:text-rubric-red'
              }`}
            >
              <BookOpen className="h-4 w-4 text-amber-700" />
              <span>Guide</span>
            </Link>
          </div>

          {/* Quick search button for mobile */}
          <Button 
            variant="ghost" 
            size="icon" 
            className="text-ink-primary hover:text-rubric-red hover:bg-paper-dark h-10 w-10"
            onClick={() => navigate('/recherche')}
          >
            <Search className="h-5 w-5" strokeWidth={1.5} />
          </Button>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 min-h-0">
        {children}
      </main>

      {/* Footer - Simplified for mobile */}
      <footer className="border-t border-border bg-paper-dark py-4 sm:py-6 mt-auto safe-area-inset-bottom">
        <div className="container mx-auto px-4 text-center">
          <p className="text-xs sm:text-sm text-ink-secondary font-body">
            La Sainte Bible  Traduction Fillion  1920
          </p>
          <p className="text-xs text-ink-secondary mt-1 italic hidden sm:block">
            « Omnis Scriptura divinitus inspirata »
          </p>
          <Link 
            to="/confidentialite" 
            className="text-xs text-ink-secondary hover:text-amber-700 mt-2 inline-block"
          >
            Politique de confidentialité
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
